import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi, type AuthUser, type LoginPayload, type RegisterPayload } from './api'
import { ApiError } from '@/shared/api-client/client'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => user.value !== null)
  const isPaid = computed(() => user.value?.plan === 'garage')
  /** Демо-аккаунт: работает, но с лимитами и без свободного скачивания. */
  const isAnonymous = computed(() => user.value?.is_anonymous === true)
  const downloadsLeft = computed(() => user.value?.anon_downloads_left ?? null)

  function clearError() {
    error.value = null
  }

  /** Called once on app boot — silently initialises from server session. */
  async function initFromSession(): Promise<void> {
    try {
      user.value = await authApi.me()
    } catch {
      user.value = null
    }
  }

  /**
   * Стартует демо-сессию. Если юзер уже залогинен — ничего не делаем,
   * иначе кнопка «Попробовать» выкинула бы его из своего аккаунта.
   */
  async function startAnonymous(): Promise<void> {
    if (user.value !== null) return
    loading.value = true
    error.value = null
    try {
      user.value = await authApi.anonymous()
    } catch (e) {
      error.value = e instanceof ApiError ? e.detail : 'Не удалось запустить демо'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function register(payload: RegisterPayload): Promise<void> {
    loading.value = true
    error.value = null
    try {
      user.value = await authApi.register(payload)
    } catch (e) {
      error.value = e instanceof ApiError ? e.detail : 'Ошибка регистрации'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function login(payload: LoginPayload): Promise<void> {
    loading.value = true
    error.value = null
    try {
      user.value = await authApi.login(payload)
    } catch (e) {
      error.value = e instanceof ApiError ? e.detail : 'Неверный email или пароль'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function logout(): Promise<void> {
    try {
      await authApi.logout()
    } finally {
      user.value = null
    }
  }

  /** Silent token refresh — called by the API client on 401. */
  async function refresh(): Promise<boolean> {
    try {
      user.value = await authApi.refresh()
      return true
    } catch {
      user.value = null
      return false
    }
  }

  async function loginWithTelegram(tgData: Parameters<typeof authApi.telegramCallback>[0]): Promise<void> {
    loading.value = true
    error.value = null
    try {
      user.value = await authApi.telegramCallback(tgData)
    } catch (e) {
      error.value = e instanceof ApiError ? e.detail : 'Ошибка входа через Telegram'
      throw e
    } finally {
      loading.value = false
    }
  }

  return {
    user,
    loading,
    error,
    isAuthenticated,
    isPaid,
    isAnonymous,
    downloadsLeft,
    clearError,
    initFromSession,
    startAnonymous,
    register,
    login,
    logout,
    refresh,
    loginWithTelegram,
  }
})
